<template>
  <section>
    <ContactModale
      :open="openContactModale"
      :total="total"
      :grae-id="graeId"
      :filters="activeFilters"
      @close-modale="openContactModale = false"
    />
    <div v-if="!graeId && hasRightsToDeleteAllInscriptionsInBrouillon() && nbBrouillons > 0">
      <b-button
        v-if="!isDeletingBrouillons"
        class="button is-danger"
        outlined
        @click="deleteAllInscriptionsInBrouillon"
      >
        {{ $t("graePage.details.projets.delete-brouillon", [nbBrouillons]) }}
      </b-button>
      <b-message v-if="isDeletingBrouillons" type="is-warning">
        {{ $t("graePage.details.projets.deleting-brouillon") }}
      </b-message>
      <br /><br />
    </div>
    <div class="year-navigation">
      <b-button
        class="button is-primary"
        :disabled="selectedAnneeScolaire === anneesScolaires[anneesScolaires.length - 1]"
        outlined
        @click="previousYear"
      >
        {{ $t("graePage.details.projets.precedent") }}
      </b-button>
      <b-select v-model="selectedAnneeScolaire" class="fullwidth" @update:model-value="goToYear">
        <template v-for="option in anneesScolaires" :key="option">
          <option :value="option">
            {{ option }}
          </option>
        </template>
      </b-select>
      <b-button
        :disabled="selectedAnneeScolaire === anneesScolaires[0]"
        class="button is-primary"
        outlined
        @click="nextYear"
      >
        {{ $t("graePage.details.projets.suivant") }}
      </b-button>
    </div>
    <div>
      <TableFilterView
        :columns="columns"
        :initial-filters="activeFilters"
        :can-export-csv="false"
        @update-active-filters="updateActiveFilters"
        @update-visible-columns="updateVisibleColumns"
        @export-csv="exportProjectsCsv"
        @export-xls="exportProjectsXls"
        @export-xls-all="exportProjectsXlsAll"
      >
        <b-button
          v-if="!isObservateurNational"
          class="ml-2"
          type="is-primary"
          @click="concatContactsForProjects()"
        >
          {{ $t("graePage.details.projets.contact-projets", [total]) }}
        </b-button>
      </TableFilterView>
      <b-table
        :ref="tableRef"
        :striped="config.isStriped"
        :hoverable="config.isHoverable"
        :debounce-search="config.searchDebounce"
        :aria-next-label="config.nextLabel()"
        :aria-previous-label="config.previousLabel()"
        :aria-page-label="config.pageLabel()"
        :aria-current-label="config.currentPageLabel()"
        :per-page="config.perPage"
        :sticky-header="config.stickyHeader"
        :current-page="currentPagedSortedParameter.pageNumber"
        :pagination-position="config.paginationPosition"
        paginated
        backend-pagination
        :sort-icon="config.sortIcon"
        :sort-icon-size="config.sortIconSize"
        backend-sorting
        sort-multiple
        :sort-multiple-data="buefySortingPriority"
        :total="total"
        :data="projectsList"
        :loading="isLoading"
        @sort="onSort"
        @sorting-priority-removed="sortingPriorityRemoved"
        @page-change="onPageChange"
      >
        <b-table-column
          v-for="column in columns"
          :key="column.field"
          v-bind="column"
          :visible="column.visible"
        >
          <template #default="props">
            <div
              class="clickable"
              @click="showProjectDetailsPage($event, props.row['projetId'], props.row['id'])"
            >
              <!-- default fields -->
              <div v-if="column.field === 'etablissement.name'">
                <div v-if="props.row['etablissements']">
                  <div
                    v-for="organisme in props.row['etablissements'].filter(
                      (o: any) => o && o.id && o.nom,
                    )"
                    :key="props.row['id'] + '_' + organisme.id"
                  >
                    {{ organisme.nom }}
                  </div>
                </div>
              </div>
              <div v-if="column.field === 'etablissement.codeEtablissement'">
                <div v-if="props.row['etablissements']">
                  <div
                    v-for="organisme in props.row['etablissements'].filter(
                      (o: any) => o && o.id && o.nom,
                    )"
                    :key="props.row['id'] + '_' + organisme.codeEtablissement"
                  >
                    {{ organisme.codeEtablissement }}
                  </div>
                </div>
              </div>
              <div v-if="column.field === 'structure.name'">
                <div v-if="props.row['structures']">
                  <div
                    v-for="organisme in props.row['structures'].filter(
                      (o: any) => o && o.id && o.nom,
                    )"
                    :key="organisme.id"
                  >
                    {{ organisme.nom }}
                  </div>
                </div>
              </div>
              <div v-if="column.field === 'projet.nom'">
                {{ props.row["nom"] }}
              </div>
              <div v-if="column.field === 'statut'">
                <StatutDossierTag
                  v-if="loggedUser"
                  :dossier="props.row"
                  :can-see-instruction-comment="false"
                  :logged-user="loggedUser"
                />
              </div>
              <div v-if="column.field === 'projet.etat'">
                <div v-if="props.row['etat'] != 'ARCHIVE'">
                  {{ $t(props.row["etat"]) }}
                </div>
                <div v-else-if="props.row['typeDossier'] == 'DOSSIER_INSCRIPTION'">
                  {{ $t("projetsListPage.archive.etat") }}
                </div>
                <div v-else>{{ $t("projetsListPage.arret.etat") }}</div>
              </div>
              <div v-if="column.field === 'nature'">
                {{ props.row["natureAireEducative"] }}
              </div>
              <div v-if="column.field === 'grae.region'">
                <span v-if="props.row['grae']">{{ props.row["grae"].region }}</span>
              </div>

              <!-- hidden fields -->
              <div v-if="column.field === 'codeDepartement'">
                <div v-if="props.row['etablissements'].length">
                  {{ props.row["etablissements"][0].codeDepartement.replace(/^0+/, "") }}
                </div>
              </div>
              <div v-if="column.field === 'dateModification'">
                {{ formatDate(props.row[column.field]) }}
              </div>
              <div v-if="column.field === 'thematique'" class="clamp">
                <span v-for="(thematique, index) in props.row[column.field]" :key="thematique">
                  {{ $t("instruction." + thematique)
                  }}<span v-if="index != props.row[column.field].length - 1">{{
                    $t("virgule")
                  }}</span>
                </span>
              </div>
              <div v-if="column.field === 'objectsConcretRealisation'" class="clamp">
                <span v-for="(objet, index) in props.row[column.field]" :key="objet">
                  {{ $t("instruction." + objet)
                  }}<span v-if="index != props.row[column.field].length - 1">{{
                    $t("virgule")
                  }}</span>
                </span>
              </div>
              <div
                v-if="column.field === 'milieuGenerique' && props.row[column.field]"
                class="clamp"
              >
                <span>{{ $t("instruction." + props.row[column.field]) }}</span>
              </div>
              <div v-if="column.field === 'milieuInteretParticulier'" class="clamp">
                <span v-for="(milieu, index) in props.row[column.field]" :key="milieu">
                  {{ $t("instruction." + milieu)
                  }}<span v-if="index != props.row[column.field].length - 1">{{
                    $t("virgule")
                  }}</span>
                </span>
              </div>
              <div v-if="column.field === 'ville'">
                <div v-if="props.row['etablissements'].length">
                  {{ props.row["etablissements"][0].ville }}
                </div>
              </div>
              <div v-if="column.field === 'enseignant.name'">
                <div
                  v-for="(utilisateur, i) in props.row['enseignants'].filter(
                    (u: any) => u && u.id && u.nom,
                  )"
                  :key="i + '_' + utilisateur.id"
                >
                  {{ utilisateur.prenom + " " + utilisateur.nom }}
                </div>
              </div>
              <div v-if="column.field === 'referent.name'">
                <div
                  v-for="(utilisateur, i) in props.row['referents'].filter(
                    (u: any) => u && u.id && u.nom,
                  )"
                  :key="i + '_' + utilisateur.id"
                >
                  {{ utilisateur.prenom + " " + utilisateur.nom }}
                </div>
              </div>
              <div v-if="column.field === 'action'">
                <div class="buttons">
                  <b-button
                    icon-left="eye"
                    type="is-link is-outlined"
                    :title="$t('consult-projet')"
                    :label="
                      loggedUser
                        ? projetsService.getProjectConsultActionLabel(props.row, loggedUser)
                        : ''
                    "
                    @click="consult($event, props.row['projetId'], props.row['id'])"
                  >
                  </b-button>
                  <b-button
                    v-if="
                      canUnarchive(props.row) &&
                      props.row['typeDossier'] == 'DOSSIER_INSCRIPTION' &&
                      hasRightsToPutInDraft()
                    "
                    type="is-success is-outlined"
                    icon-left="save"
                    :title="$t('projetsListPage.archive.undo-action')"
                    :label="$t('projetsListPage.archive.undo-action-label')"
                    @click="unarchive($event, props.row)"
                  >
                  </b-button>
                  <b-button
                    v-if="
                      canUnarchive(props.row) &&
                      props.row['typeDossier'] != 'DOSSIER_INSCRIPTION' &&
                      hasRightsToPutInDraft()
                    "
                    type="is-success is-outlined"
                    icon-left="save"
                    :title="$t('projetsListPage.arret.undo-action')"
                    :label="$t('projetsListPage.arret.undo-action-label')"
                    @click="unarchive($event, props.row)"
                  >
                  </b-button>
                  <b-button
                    v-if="
                      canUnlock(props.row) && isInstruction(props.row) && hasRightsToPutInDraft()
                    "
                    type="is-success is-outlined"
                    icon-left="save"
                    :title="$t('projetsListPage.brouillon.undo-action')"
                    :label="$t('projetsListPage.brouillon.undo-action-label')"
                    @click="unlock($event, props.row)"
                  >
                  </b-button>
                </div>
              </div>
              <div v-if="column.field === 'typeDossier'">
                {{ $t(props.row.typeDossier) }}
              </div>

              <!-- non customised fields -->
              <div v-if="!column.custom">
                <div>{{ props.row[column.field] }}</div>
              </div>
            </div>
          </template>
        </b-table-column>
      </b-table>
    </div>
  </section>
</template>

<script lang="ts">
import { BOOLEAN, EnumHelper, PROFIL_UTILISATEUR } from "@/model/bean/EnumHelper"
import {
  FilterDTO,
  PaginationOrder,
  PaginationParameter,
  ProjetAnnuelDTO,
  UtilisateurDTO,
} from "@/model/bean/GeneratedDTOs"
import { Filters } from "@/model/bean/Filters"
import RouterUtils from "@/router/RouterUtils"
import StatutDossierTag from "@/views/dossier/StatutDossierTag.vue"
import Column from "@/views/tables/Column"
import ContactModale from "@/views/commons/ContactModale.vue"
import TableFilterView from "@/views/tables/TableFilterView.vue"
import { defineComponent } from "vue"
import { InfoReportingService } from "@/services/log/InfoReportingService"
import { ProjectService } from "@/services/rest/project/ProjectService"
import { UsersService } from "@/services/rest/users/UsersService"
import { InstructionService } from "@/services/rest/instruction/InstructionService"
import { GraeService } from "@/services/rest/grae/GraeService"
import { LoginService } from "@/services/rest/login/LoginService"
import { OrganismeService } from "@/services/rest/organisme/OrganismeService"
import TabParamsStore from "@/services/storage/TabParamsStore"
import { URL_PREFIX_GRAE_ETABLISSEMENT } from "@/views/grae/GraeEtablissementsView.vue"
import { URL_PREFIX_GRAE_STRUCTURE } from "@/views/grae/GraeStructuresView.vue"
import { useTableView } from "@/hooks/useTableView"
import { SortOrder } from "@/model/SortOrder"
import { BButton, BMessage, BTable, BTableColumn, BSelect } from "buefy"
import DateUtils from "@/services/DateUtils"

const HISTORY_PAGINATION_GRAE_PROJECT = "history.pagination.grae.project"
export const URL_PREFIX_GRAE_PROJECT = "/project/"

export default defineComponent({
  name: "GraeProjectsView",
  components: {
    TableFilterView,
    StatutDossierTag,
    ContactModale,
    BButton,
    BSelect,
    BMessage,
    BTable,
    BTableColumn,
  },
  props: {
    graeId: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      loginService: LoginService.INSTANCE,
      instructionService: InstructionService.INSTANCE,
      infoReportingService: InfoReportingService.INSTANCE,
      projetsService: ProjectService.INSTANCE,
      organismeService: OrganismeService.INSTANCE,
      usersService: UsersService.INSTANCE,
      graeService: GraeService.INSTANCE,

      loggedUser: new UtilisateurDTO() as UtilisateurDTO | undefined,
      projectsList: [] as Array<ProjetAnnuelDTO>,
      nbBrouillons: 0,
      graeNamesList: [] as Array<string>,
      isDeletingBrouillons: false,
      openContactModale: false,
      contacts: [] as UtilisateurDTO[],
      isObservateurNational: false,
      anneesScolaires: [] as Array<string>,
      selectedAnneeScolaire: "",

      // Utilisation du hook useTableView - sera initialisé dans created
      tableView: null as any,
    }
  },
  computed: {
    // Propriétés réactives exposées par le hook
    tableRef(): string {
      return this.tableView?.state.tableRef || "grae-projects-table"
    },
    isLoading(): boolean {
      return this.tableView?.state.isLoading || false
    },
    columns(): Array<Column> {
      return this.tableView?.state.columns || []
    },
    currentPagedSortedParameter(): any {
      return this.tableView?.state.currentPagedSortedParameter
    },
    buefySortingPriority(): Array<any> {
      return this.tableView?.state.buefySortingPriority || []
    },
    total(): number {
      return this.tableView?.state.total || 0
    },
    config(): any {
      return this.tableView?.state.config
    },
    perPage(): number {
      return this.tableView?.state.perPage || 25
    },
    activeFilters: {
      get(): Array<any> {
        return this.tableView?.state.activeFilters || []
      },
      set(value: Array<any>): void {
        if (this.tableView?.state) {
          this.tableView.state.activeFilters = value
        }
      },
    },
  },
  created() {
    // Initialisation du tableView
    this.tableView = useTableView({
      tableRef: "grae-projects-table",
      router: this.$router,
      route: this.$route,
      i18n: this.$i18n,
      getInitialColumns: this.getInitialColumns.bind(this),
      getDefaultSort: this.getDefaultSort.bind(this),
      getInitialActiveFilters: this.getInitialActiveFilters.bind(this),
      reloadData: this.reloadData.bind(this),
      reloadDataWhenMounted: false,
      updateLocalStorageHistoryPagination: this.updateLocalStorageHistoryPagination.bind(this),
    })

    // Logique métier d'initialisation
    this.loggedUser = this.loginService.getLoggedUser()
    if (!this.loggedUser || !this.loggedUser.id) {
      return
    } else {
      this.isObservateurNational = this.usersService.isObservateurNational(this.loggedUser)
    }
    if (!this.graeId && this.hasRightsToDeleteAllInscriptionsInBrouillon()) {
      this.countNbBrouillons()
    }

    // Get region lists for filtering purposes
    if (!this.graeId) {
      this.organismeService.getAllGraes().then((graes) => {
        this.graeNamesList = graes.map((g) => g.region)
        this.refreshInitialColumns()
      })
    }
  },
  mounted() {
    this.tableView?.initMounted()

    this.setPreviousPagination(
      ["/ae/", URL_PREFIX_GRAE_PROJECT, URL_PREFIX_GRAE_STRUCTURE, URL_PREFIX_GRAE_ETABLISSEMENT],
      HISTORY_PAGINATION_GRAE_PROJECT,
    )

    this.loadAnneesScolaires()
  },
  methods: {
    // Méthodes déléguées au hook
    updateVisibleColumns(newVisibleColumnList: Array<Column>): void {
      this.tableView?.updateVisibleColumns(newVisibleColumnList)
    },
    onPageChange(newPage: string | number): void {
      const pageNum = typeof newPage === "string" ? parseInt(newPage) : newPage
      this.tableView.onPageChange(pageNum)
    },

    onSort(field: string | undefined, order: SortOrder): void {
      if (field) {
        this.tableView.onSort(field, order)
      }
    },

    sortingPriorityRemoved(field: string | undefined): void {
      if (field) {
        this.tableView.sortingPriorityRemoved(field)
      }
    },
    setPreviousPagination(activePrefixUrls: string[], historyPaginationStorageKey: string): void {
      this.tableView?.setPreviousPagination(activePrefixUrls, historyPaginationStorageKey)
    },
    setLocalStorageHistoryPagination(historyPaginationStorageKey: string): void {
      this.tableView?.setLocalStorageHistoryPagination(historyPaginationStorageKey)
    },
    refreshInitialColumns(): void {
      this.tableView?.refreshInitialColumns()
    },
    updateTotal(total: number): void {
      this.tableView?.updateTotal(total)
    },

    // Méthodes pour remplacer les setters
    setIsLoading(value: boolean): void {
      if (this.tableView) {
        this.tableView.state.isLoading = value
      }
    },
    setActiveFilters(value: Array<any>): void {
      if (this.tableView) {
        this.tableView.state.activeFilters = value
      }
    },

    // Méthodes callbacks requises par le hook
    updateLocalStorageHistoryPagination(): void {
      this.setLocalStorageHistoryPagination(HISTORY_PAGINATION_GRAE_PROJECT)
    },

    async countNbBrouillons(): Promise<void> {
      const page = await this.instructionService.getInscriptions(
        undefined,
        this.paginationParameter(),
        this.inscriptionsFilters(),
        undefined,
      )
      this.nbBrouillons = page.count
    },

    paginationParameter(): PaginationParameter {
      const paginationParameter = new PaginationParameter()
      paginationParameter.pageNumber = 1
      paginationParameter.pageSize = 1
      return paginationParameter
    },

    inscriptionsFilters(): Array<FilterDTO> {
      const filters = new Array<FilterDTO>()
      filters.push(Filters.asFilter({ property: "statut", values: ["BROUILLON"] }))
      return filters
    },

    async reloadData(): Promise<void> {
      try {
        const paginatedProjectsList = await this.instructionService.getProjects(
          this.graeId,
          this.currentPagedSortedParameter,
          this.activeFilters,
        )
        this.projectsList = paginatedProjectsList.elements
        this.updateTotal(paginatedProjectsList.count)

        const newFiltersInQuery = JSON.stringify(this.activeFilters)

        const existingFiltersQueryParam = Array.isArray(this.$route.query.filters)
          ? this.$route.query.filters[0]
          : this.$route.query.filters

        const existingFiltersJson =
          typeof existingFiltersQueryParam === "string"
            ? decodeURIComponent(existingFiltersQueryParam)
            : undefined

        if (existingFiltersJson !== newFiltersInQuery) {
          const searchParams = new URLSearchParams()

          Object.entries(this.$route.query).forEach(([key, rawValue]) => {
            if (key === "filters") {
              return
            }

            if (Array.isArray(rawValue)) {
              rawValue.forEach((value) => {
                if (typeof value === "string") {
                  searchParams.append(key, value)
                }
              })
            } else if (typeof rawValue === "string") {
              searchParams.set(key, rawValue)
            }
          })

          const encodedFilters = encodeURIComponent(newFiltersInQuery)
          searchParams.set("filters", encodedFilters)

          const queryString = searchParams.toString()
          const hashSuffix = this.$route.hash ? this.$route.hash : ""
          const targetFullPath = `${this.$route.path}${queryString ? `?${queryString}` : ""}${hashSuffix}`

          if (targetFullPath !== this.$route.fullPath) {
            await this.$router.push(targetFullPath)
          }
        }
      } catch (e: any) {
        // Silent catch
      }
    },

    invited(user: UtilisateurDTO, project: ProjetAnnuelDTO): boolean {
      if (user.id) {
        return project.consentementEnAttente.indexOf(user.id) !== -1
      } else {
        return false
      }
    },

    concatContactsForProjects(): void {
      this.contacts = []
      for (const project of this.projectsList) {
        const enseignants = project.enseignants
          ? project.enseignants.filter((e) => !this.invited(e, project))
          : []
        const referents = project.referents
          ? project.referents.filter((r) => !this.invited(r, project))
          : []
        const tab = enseignants.concat(referents)
        this.contacts = this.contacts.concat(tab)
      }
      this.openContactModale = true
    },

    getInitialColumns(): Array<Column> {
      const initialColumns: Array<Column> = [
        // default fields
        {
          field: "etablissement.name",
          label: this.$t("graePage.details.projets.etablissements").toString(),
          sortable: false,
          backEndSearchable: true,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "etablissement.codeEtablissement",
          label: this.$t("graePage.details.projets.uais").toString(),
          sortable: false,
          backEndSearchable: true,
          visible: false,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "structure.name",
          label: this.$t("graePage.details.projets.structures").toString(),
          sortable: false,
          backEndSearchable: true,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "projet.nom",
          label: this.$t("graePage.details.projets.nom").toString(),
          sortable: true,
          backEndSearchable: true,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "typeDossier",
          label: this.$t("graePage.details.projets.typeDossier").toString(),
          sortable: true,
          backEndSearchable: true,
          possibleSearchValues: EnumHelper.allTypeDossiers,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "statut",
          label: this.$t("graePage.details.projets.statut").toString(),
          sortable: true,
          backEndSearchable: true,
          possibleSearchValues: EnumHelper.allStatusWithoutArchivedAndPassable,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "projet.etat",
          label: this.$t("graePage.details.projets.etat").toString(),
          sortable: true,
          backEndSearchable: true,
          possibleSearchValues: EnumHelper.allEtatsProjets,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "nature",
          label: this.$t("graePage.details.projets.nature").toString(),
          sortable: true,
          backEndSearchable: true,
          possibleSearchValues: EnumHelper.allNatures,
          visible: true,
          custom: true,
          canBeAdded: true,
        },
        // hidden fields
        {
          field: "codeDepartement",
          label: this.$t("graePage.details.projets.departement").toString(),
          sortable: false,
          backEndSearchable: true,
          visible: false,
          custom: false,
          canBeAdded: true,
        },
        {
          field: "dateModification",
          label: this.$t("graePage.details.projets.dateModification").toString(),
          sortable: false,
          backEndSearchable: true,
          visible: false,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "thematique",
          label: this.$t("graePage.details.projets.thematiques").toString(),
          sortable: false,
          backEndSearchable: false,
          visible: false,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "objectsConcretRealisation",
          label: this.$t("graePage.details.projets.objets-concrets-de-realisation").toString(),
          sortable: false,
          backEndSearchable: false,
          visible: false,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "milieuGenerique",
          label: this.$t("graePage.details.projets.milieu-generique").toString(),
          sortable: false,
          backEndSearchable: false,
          visible: false,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "milieuInteretParticulier",
          label: this.$t("graePage.details.projets.milieux-interets-particuliers").toString(),
          sortable: false,
          backEndSearchable: false,
          visible: false,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "ville",
          label: this.$t("graePage.details.projets.ville").toString(),
          sortable: false,
          backEndSearchable: true,
          visible: false,
          custom: false,
          canBeAdded: true,
        },
        {
          field: "enseignant.name",
          label: this.$t("graePage.details.projets.enseignants").toString(),
          sortable: false,
          backEndSearchable: true,
          visible: false,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "referent.name",
          label: this.$t("graePage.details.projets.referents").toString(),
          sortable: false,
          backEndSearchable: true,
          visible: false,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "lastEvaluationInstructeurNames",
          label: this.$t("graePage.details.projets.instructeurs").toString(),
          sortable: false,
          backEndSearchable: true,
          visible: false,
          custom: false,
          canBeAdded: true,
        },
        {
          field: "action",
          label: this.$t("projetsListPage.actions").toString(),
          sortable: false,
          backEndSearchable: false,
          visible: false,
          custom: true,
          canBeAdded: true,
        },
        {
          field: "inscriptionThisYear",
          label: this.$t("graePage.details.projets.inscriptionThisYear").toString(),
          sortable: true,
          backEndSearchable: true,
          possibleSearchValues: Object.values(BOOLEAN).map((v) => v.toString()),
          visible: false,
          custom: false,
          canBeAdded: true,
        },
      ]
      if (!this.graeId) {
        initialColumns.push({
          field: "grae.region",
          label: this.$t("graeList.region").toString(),
          possibleSearchValues: this.graeNamesList,
          sortable: true,
          backEndSearchable: true,
          visible: true,
          custom: true,
          canBeAdded: true,
        })
      }
      return initialColumns
    },

    getInitialActiveFilters(): Array<FilterDTO> {
      const filtersQuery = this.$route.query?.filters
      const filtersQueryValue = Array.isArray(filtersQuery) ? filtersQuery[0] : filtersQuery

      if (typeof filtersQueryValue === "string") {
        let decodedFiltersJson = filtersQueryValue
        try {
          decodedFiltersJson = decodeURIComponent(filtersQueryValue)
        } catch (_e) {
          // Not encoded, keep as-is
        }

        const filters = JSON.parse(decodedFiltersJson) as FilterDTO[]

        // Le filtre "anneeScolaire n'est pas a mettre avec les autres filtres,
        // mais dans la sélection en haut
        const yearFilter = filters.find((f) => f.property == "anneeScolaire")
        if (yearFilter && yearFilter.values.length == 1) {
          this.selectedAnneeScolaire = yearFilter.values[0]
        }

        return filters.filter((f) => f.property !== "anneeScolaire")
      }
      return new Array<FilterDTO>()
    },

    getDefaultSort(): Array<PaginationOrder> {
      const defaultSort = new PaginationOrder()
      defaultSort.clause = "projet.nom"
      defaultSort.desc = true
      return [defaultSort]
    },

    showProjectDetailsPage(event: Event, projetId: string, projetAnnuelId: string): void {
      // Do not foward click event to row (would trigger modal)
      event.stopPropagation()
      this.$router.push(RouterUtils.getRouteForProjectDetails(projetId, projetAnnuelId))
    },

    consult(event: Event, projetId: string, projetAnnuelId: string): void {
      // Do not foward click event to row (would trigger modal)
      event.stopPropagation()
      const route = this.$router.resolve(
        RouterUtils.getRouteForProjectDetails(projetId, projetAnnuelId),
      )
      window.open(route.href)
    },

    formatDate(date: Date): string {
      return DateUtils.formatDate(date)
    },

    canUnarchive(projet: ProjetAnnuelDTO): boolean {
      if (projet.etat == "ARCHIVE") {
        const projectLastModificationDate = new Date(
          projet.dateModification ? projet.dateModification : "2000-01-01",
        )
        const projetLastModicationTime = projectLastModificationDate.getTime()
        const diffFromNow = new Date().getTime() - projetLastModicationTime
        const diffFromNowInDays = diffFromNow / (1000 * 3600 * 24)
        return 365 > diffFromNowInDays
      }
      return false
    },

    canUnlock(projet: ProjetAnnuelDTO): boolean {
      if (projet.etat == "EN_COURS_DE_CREATION" || projet.statut == "INSTRUCTION_EN_COURS") {
        const projectLastModificationDate = new Date(
          projet.dateModification ? projet.dateModification : "2000-01-01",
        )
        const projetLastModicationTime = projectLastModificationDate.getTime()
        const diffFromNow = new Date().getTime() - projetLastModicationTime
        const diffFromNowInDays = diffFromNow / (1000 * 3600 * 24)
        return 365 > diffFromNowInDays
      }
      return false
    },

    isInstruction(projet: ProjetAnnuelDTO): boolean {
      return projet.statut == "EN_ATTENTE" || projet.statut == "INSTRUCTION_EN_COURS"
    },

    async unarchive(event: Event, projet: ProjetAnnuelDTO): Promise<void> {
      event.stopPropagation()
      this.infoReportingService.dialog(
        this.$t("projetsListPage.archive.confirm-title").toString(),
        this.$t("projetsListPage.archive.confirm-message").toString(),
        this.$t("projetsListPage.archive.confirm-action").toString(),
        async () => {
          if (projet.projetId) {
            await this.projetsService.unarchiveProject(projet.projetId)
          }
          await this.tableView.reload()
        },
        "is-warning",
      )
    },

    async unlock(event: Event, projet: ProjetAnnuelDTO): Promise<void> {
      event.stopPropagation()
      this.infoReportingService.dialog(
        this.$t("projetsListPage.brouillon.confirm-title").toString(),
        this.$t("projetsListPage.brouillon.confirm-message").toString(),
        this.$t("projetsListPage.brouillon.confirm-action").toString(),
        async () => {
          if (projet.projetId) {
            await this.projetsService.unlockProject(projet.projetId)
          }
          await this.tableView.reload()
        },
        "is-warning",
      )
    },

    hasRightsToPutInDraft(): boolean {
      return UsersService.INSTANCE.hasAnyProfile(this.loggedUser, [
        PROFIL_UTILISATEUR.ACCOMPAGNATEUR_NATIONAL,
        PROFIL_UTILISATEUR.SUPER_ADMINISTRATEUR,
        PROFIL_UTILISATEUR.ANIMATEUR_REGIONAL,
      ])
    },

    hasRightsToDeleteAllInscriptionsInBrouillon(): boolean {
      return UsersService.INSTANCE.hasAnyProfile(this.loggedUser, [
        PROFIL_UTILISATEUR.ACCOMPAGNATEUR_NATIONAL,
        PROFIL_UTILISATEUR.SUPER_ADMINISTRATEUR,
      ])
    },

    deleteAllInscriptionsInBrouillon(): void {
      this.infoReportingService.dialogWithCancel(
        this.$t("graePage.details.projets.delete-brouillon", [this.nbBrouillons]).toString(),
        this.$t("graePage.details.projets.confirm-popup-message", [this.nbBrouillons]).toString(),
        this.$t("graePage.details.projets.confirm-popup-ok-button").toString(),
        async () => {
          this.isDeletingBrouillons = true
          this.isLoading = true
          try {
            await this.instructionService.deleteAllInscriptionsInBrouillon()
            this.isLoading = false
            this.infoReportingService.success(
              this.$t("graePage.details.projets.deleted-inscriptions").toString(),
            )
            this.isDeletingBrouillons = false
            await this.tableView.reload()
            await this.countNbBrouillons()
          } catch (e: any) {
            this.isLoading = false
            this.infoReportingService.error(
              this.$t("graePage.details.projets.delete-inscriptions-failure").toString(),
              e,
            )
            this.isDeletingBrouillons = false
          }
        },
        () => {
          // Nothing to do
        },
        "is-danger",
      )
    },

    exportProjectsCsv(): void {
      this.instructionService.exportProjectsCsv(this.graeId, this.activeFilters)
    },

    exportProjectsXls(): void {
      this.instructionService.exportProjectsXls(this.graeId, this.activeFilters)
    },

    exportProjectsXlsAll(): void {
      this.instructionService.exportProjectsXls(
        this.graeId,
        this.activeFilters.filter((f) => f.property != "anneeScolaire"),
      )
    },

    previousYear(): void {
      const thisYearIndex = this.anneesScolaires.indexOf(this.selectedAnneeScolaire)
      const previousYear = this.anneesScolaires[thisYearIndex + 1]

      this.clearAnneeScolaireFilter()

      this.activeFilters.push(
        Filters.asFilter({ property: "anneeScolaire", values: [previousYear] }),
      )
      this.tableView.reload()
      this.selectedAnneeScolaire = previousYear
    },

    nextYear(): void {
      const thisYearIndex = this.anneesScolaires.indexOf(this.selectedAnneeScolaire)
      const nextYear = this.anneesScolaires[thisYearIndex - 1]

      this.clearAnneeScolaireFilter()

      this.activeFilters.push(Filters.asFilter({ property: "anneeScolaire", values: [nextYear] }))
      this.tableView.reload()
      this.selectedAnneeScolaire = nextYear
    },

    goToYear(newValue?: string): void {
      if (newValue) {
        this.selectedAnneeScolaire = newValue
      }
      this.$nextTick(() => {
        this.clearAnneeScolaireFilter()

        this.activeFilters.push(
          Filters.asFilter({ property: "anneeScolaire", values: [this.selectedAnneeScolaire] }),
        )
        this.tableView.reload()
      })
    },

    async loadAnneesScolaires(): Promise<void> {
      if (this.graeId) {
        this.anneesScolaires = await this.graeService.getAnneesScolairesForGRAE(this.graeId)
      } else {
        this.anneesScolaires = await this.graeService.getAnneesScolaires()
      }

      this.selectedAnneeScolaire ||= this.anneesScolaires[0]
      this.goToYear()
    },

    clearAnneeScolaireFilter(): void {
      this.activeFilters = this.activeFilters.filter((item) => item.property !== "anneeScolaire")
    },

    updateActiveFilters(newActiveFilters: Array<FilterDTO>): void {
      this.activeFilters = newActiveFilters

      TabParamsStore.INSTANCE.storeFilters(
        this.$router.currentRoute.value.path + this.tableRef,
        newActiveFilters,
      )
      this.goToYear()
    },
  },
})
</script>

<style lang="scss">
.clamp {
  text-align: left;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  overflow: hidden;
}

select {
  width: 100%; /* Ensure it uses available space */
}
</style>
